import { useEffect } from "react";
import { Link, useLocation, useParams } from "wouter";
import {
  LayoutDashboard,
  Package,
  FileUp,
  Users,
  Building,
  Activity,
  PanelLeftClose,
  PanelLeftOpen
} from "lucide-react";
import { cn } from "@/lib/utils";

interface TendererAuth {
  email: string;
  accessCode: string;
  companyName: string;
  authenticatedAt: string;
}

interface Project {
  id: string;
  name: string;
  packageCount: number;
}

interface TendererSidebarProps {
  open: boolean;
  selectedProject: Project | null;
  authData: TendererAuth;
  toggleSidebar: () => void;
}

export function TendererSidebar({ open, selectedProject, authData, toggleSidebar }: TendererSidebarProps) {
  const [location] = useLocation();
  const params = useParams<{ companyCode?: string; projectId?: string }>() || {};

  // Generate company code from auth data
  const companyCode = authData.companyName.replace(/\s+/g, '-').toLowerCase();
  const projectId = selectedProject?.id || params.projectId;

  // Handle sidebar visibility on mobile
  useEffect(() => {
    const sidebar = document.getElementById("tenderer-sidebar");
    if (sidebar) {
      // Only handle mobile visibility, desktop should always be visible
      if (window.innerWidth < 768) { // md breakpoint
        if (open) {
          sidebar.classList.remove("hidden");
          sidebar.classList.add("fixed", "left-0", "top-0", "z-50");
        } else {
          sidebar.classList.add("hidden");
        }
      } else {
        // Desktop: always visible, just toggle width
        sidebar.classList.remove("hidden");
      }
    }
  }, [open]);

  const getNavigationPath = (page: string) => {
    if (projectId && authData.accessCode) {
      // Use unified URL structure: /:page/:projectId/tenderer/:companyCode
      return `/${page}/${projectId}/tenderer/${authData.accessCode}`;
    }
    return "#"; // No valid navigation without project
  };

  const menuItems = [
    {
      page: "dashboard",
      navPath: `/dashboard/${projectId}/tenderer/${authData.accessCode}`,
      label: "Dashboard",
      icon: <LayoutDashboard className="h-5 w-5" />,
    },
    {
      page: "packages",
      navPath: getNavigationPath("packages"),
      label: "Tender Packages",
      icon: <Package className="h-5 w-5" />,
    },
    {
      page: "returns",
      navPath: getNavigationPath("returns"),
      label: "Tender Returns",
      icon: <FileUp className="h-5 w-5" />,
    },
    {
      page: "members",
      navPath: getNavigationPath("members"),
      label: "Members",
      icon: <Users className="h-5 w-5" />,
    },
  ];

  const isActiveRoute = (page: string) => {
    return location.includes(`/${page}/`) && location.includes(`/tenderer/`);
  };

  return (
    <div
      id="tenderer-sidebar"
      className={cn(
        "w-full bg-gradient-to-b from-slate-800 via-slate-800 to-slate-900 md:min-h-screen overflow-y-auto md:fixed md:left-0 md:top-0 md:z-50 shadow-2xl transition-all duration-300 scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-transparent hover:scrollbar-thumb-gray-400",
        open ? "md:w-64" : "md:w-16"
      )}
    >
      {/* Logo/Brand */}
      <div className={cn(
        "p-4 flex items-center border-b border-slate-700 h-14",
        open ? "justify-start" : "justify-center"
      )}>
        <div className="flex items-center">
          <Building className={cn(
            "h-6 w-6 text-emerald-400",
            open ? "mr-3" : "mr-0"
          )} />
          <span className={cn(
            "text-white font-bold text-base whitespace-nowrap transition-opacity duration-300",
            open ? "opacity-100" : "opacity-0 md:hidden"
          )}>HG Tender Hub</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="mt-4">

        {menuItems.map((item) => {
          const isActive = isActiveRoute(item.page);
          
          return (
            <Link key={item.page} href={item.navPath}>
              <div
                className={cn(
                  "mx-4 my-1 flex items-center rounded-xl px-4 py-2.5 text-base font-semibold transition-all duration-200",
                  isActive
                    ? "bg-emerald-600/80 text-white shadow-md"
                    : "text-slate-300 hover:bg-slate-700 hover:text-white",
                  !open && "md:justify-center md:px-2"
                )}
                title={!open ? item.label : undefined}
              >
                <div className={cn(
                  "flex items-center justify-center",
                  open ? "mr-3" : "mr-0"
                )}>
                  {item.icon}
                </div>
                <span className={cn(
                  "transition-opacity duration-300",
                  open ? "opacity-100" : "opacity-0 md:hidden"
                )}>
                  {item.label}
                </span>
              </div>
            </Link>
          );
        })}
      </nav>

      {/* Toggle Sidebar Button */}
      <div className="absolute bottom-4 left-0 right-0 px-2">
        <button
          onClick={toggleSidebar}
          className={cn(
            "flex items-center justify-center text-white rounded-md transition-all duration-200 shadow-lg",
            open 
              ? "w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-700 border-2 border-emerald-500" 
              : "w-full md:w-10 md:h-10 md:rounded-full bg-emerald-600 hover:bg-emerald-700 border-2 border-emerald-500 md:shadow-xl"
          )}
          title={open ? "Collapse Sidebar" : "Expand Sidebar"}
        >
          {open ? (
            <>
              <PanelLeftClose className="h-5 w-5 mr-2" />
              <span className="text-sm font-medium">Collapse</span>
            </>
          ) : (
            <>
              <div className="flex items-center justify-center w-full h-full">
                <PanelLeftOpen className="h-5 w-5 text-white" />
              </div>
              <span className="text-sm md:hidden font-medium">Expand</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}